import React, { useEffect, useRef, useCallback } from 'react';
import type { Point, CategorieMarqueur } from '../../types/map';
import { renderTemplate, renderFallback, buildCssVariables } from './templateRenderer';
import { CloseIcon } from '../icons/CloseIcon';
import './MarkerInfo.css';

interface MarkerModaleProps {
  point: Point;
  categorie?: CategorieMarqueur;
  onClose: () => void;
}

/**
 * Modale centrée (overlay plein écran) affichant le contenu du template.
 * Fermeture par clic sur le backdrop, touche Echap, ou bouton de fermeture.
 * 
 * Les CSS Variables sont injectées sur le wrapper pour permettre
 * l'utilisation de var(--couleur), var(--notation), etc. dans le CSS.
 */
export const MarkerModale: React.FC<MarkerModaleProps> = ({ point, categorie, onClose }) => {
  const modaleRef = useRef<HTMLDivElement>(null);

  const html = categorie?.templateHtml
    ? renderTemplate(categorie.templateHtml, point, categorie)
    : renderFallback(point);

  const scopeClass = categorie?.id ? `tpl-cat-${categorie.id}` : '';
  const sizeClass = `marker-modale--${categorie?.largeurAffichage || 'medium'}`;
  const cssVars = categorie ? buildCssVariables(point, categorie) : {};

  // Build container style from styleConteneur
  const sc = categorie?.styleConteneur;
  const containerStyle: Record<string, string> = {};
  if (sc?.borderRadius) containerStyle['--ctn-radius'] = sc.borderRadius;
  if (sc?.padding) containerStyle['--ctn-padding'] = sc.padding;
  if (sc?.background) containerStyle['--ctn-bg'] = sc.background;
  else if (categorie?.couleur) containerStyle['--ctn-bg'] = categorie.couleur;
  if (sc?.overlayBackground) containerStyle['--ctn-overlay-bg'] = sc.overlayBackground;
  if (sc?.closeButtonColor) containerStyle['--ctn-close-color'] = sc.closeButtonColor;
  if (sc?.closeButtonHoverColor) containerStyle['--ctn-close-hover-color'] = sc.closeButtonHoverColor;
  if (sc?.boxShadow) containerStyle['--ctn-shadow'] = sc.boxShadow;

  // Focus trap + Echap
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Focus la modale à l'ouverture
    modaleRef.current?.focus();
    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="marker-modale-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Détails du point : ${point.titre || point.nom}`}
      style={containerStyle as React.CSSProperties}
    >
      <div
        ref={modaleRef}
        className={`marker-modale ${sizeClass}`}
        tabIndex={-1}
      >
        <button
          className="marker-modale__close"
          onClick={onClose}
          aria-label="Fermer"
          title="Fermer"
        >
          <CloseIcon size={20} />
        </button>
        <div className="marker-modale__content">
          <div
            className={scopeClass}
            style={cssVars}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </div>
  );
};
